/**
 * 用户管理模块（OFBiz 新接口）
 * ------------------------------------------------------------------
 * 对应后端：POST /itoss/control/json/user/*
 *
 * 迁移自前端原接口（rest_client.js / setting_action.js）：
 *   getUserList  -> /rest/security/userinfos
 *   operateUser  -> /rest/security/userinfo/operate
 *   resetPassword -> /rest/security/pwd/operate/reset
 *   changePassword -> /rest/security/pwd/operate/update
 *
 * 字段映射（旧 PascalCase -> 新 camelCase）：
 *   LoginID        -> userLoginId
 *   DisplayName    -> nickname（或 firstName + lastName）
 *   GroupName      -> orgName / 安全组
 *   Roles          -> securityGroups（安全组名数组）
 *   Email/Cellphone-> email / mobile（PartyContactMech，见 user/contacts）
 *   Status(活动/已终止) -> enabled(Y/N) + Party.statusId
 *   RecId          -> partyId
 */
import { ofbiz } from './http';

/**
 * 用户列表（分页，替代旧 getUserList）
 * @param {object} [query] { keyword, ... }
 * @param {number} [page=1]
 * @param {number} [pageSize=20]
 * @returns {Promise<{list, total, page, pageSize}>}
 *   list 元素：{ partyId, userLoginId, firstName, lastName, nickname,
 *               gender, enabled, orgId, orgName, email, mobile, securityGroups }
 */
export function getUserList(query = {}, page = 1, pageSize = 20) {
  return ofbiz.postPage('user/list', query, page, pageSize);
}

/**
 * 用户详情（替代旧 operateUser 中"查详情"或 getUserList 单项）
 * @param {string} partyId
 * @returns {Promise<object>} 含 securityGroups 安全组列表
 */
export function getUserById(partyId) {
  return ofbiz.post('user/getById', null, { partyId });
}

/**
 * 新增用户（替代旧 operateUser Operate='add'）
 * @param {object} data { userLoginId(必填), password(必填),
 *                       firstName, lastName, nickname, gender,
 *                       orgId, groupIds(逗号分隔), requirePasswordChange,
 *                       email, mobile }
 * @returns {Promise<object>} { partyId, userLoginId }
 */
export function createUser(data) {
  return ofbiz.post('user/create', data);
}

/**
 * 更新用户（替代旧 operateUser Operate='edit'）
 * @param {object} data 必含 partyId；firstName/lastName/nickname/gender/orgId(空串=脱离组织)
 * @returns {Promise<object>}
 */
export function updateUser(data) {
  return ofbiz.post('user/update', data);
}

/**
 * 禁用用户（软删，替代旧 operateUser Operate='delete'）
 * @param {string} partyId
 * @returns {Promise<object>}
 */
export function deleteUser(partyId) {
  return ofbiz.post('user/delete', null, { partyId });
}

/**
 * 启用用户
 * @param {string} partyId
 * @returns {Promise<object>}
 */
export function enableUser(partyId) {
  return ofbiz.post('user/enable', null, { partyId });
}

/**
 * 管理员重置密码（替代旧 resetPassword）
 * @param {string} userLoginId
 * @param {string} newPassword
 * @param {string} [requirePasswordChange] Y/N
 * @returns {Promise<object>}
 */
export function resetPassword(userLoginId, newPassword, requirePasswordChange) {
  return ofbiz.post('user/resetPassword', null, { userLoginId, newPassword, requirePasswordChange });
}

/**
 * 分配用户安全组（覆盖式，替代旧 updateUserGroup）
 * @param {string} userLoginId
 * @param {string|string[]} groupIds 安全组 ID，逗号分隔或数组；空=清空所有角色
 * @returns {Promise<object>}
 */
export function assignGroups(userLoginId, groupIds) {
  const ids = Array.isArray(groupIds) ? groupIds.join(',') : groupIds;
  return ofbiz.post('user/assignGroups', null, { userLoginId, groupIds: ids });
}

/**
 * 读取用户联系方式 email/mobile
 * @param {string} userLoginId
 * @returns {Promise<object>} { email, mobile }
 */
export function getUserContacts(userLoginId) {
  return ofbiz.post('user/contacts', null, { userLoginId });
}

/**
 * 更新用户联系方式（覆盖式，空串=清除）
 * @param {string} userLoginId
 * @param {string} [email]
 * @param {string} [mobile]
 * @returns {Promise<object>}
 */
export function updateUserContacts(userLoginId, email, mobile) {
  return ofbiz.post('user/updateContacts', null, { userLoginId, email, mobile });
}
